import React, { useState } from 'react';
import { Crown, Calendar, CreditCard, Mail, Bell, AlertCircle, CheckCircle, X, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

const SubscriptionSettings = ({ subscription, settings, onSaveSettings, currentUser }) => {
    const navigate = useNavigate();
    const [localSettings, setLocalSettings] = useState(settings);
    const [saving, setSaving] = useState(false);
    const [loadingPortal, setLoadingPortal] = useState(false);

    const formatDate = (date) => {
        if (!date) return '';
        return date.toLocaleDateString('ro-RO', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getDaysRemaining = (endDate) => {
        if (!endDate) return 0;
        return Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
    };

    const handleToggle = (field) => {
        setLocalSettings(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSaveSettings(localSettings);
            alert('Setări salvate cu succes!');
        } catch (error) {
            console.error('Eroare la salvarea setărilor:', error);
            alert('Eroare la salvarea setărilor');
        } finally {
            setSaving(false);
        }
    };

    // Stripe Customer Portal - Apelează Cloud Function
    const handleManageSubscription = async () => {
        setLoadingPortal(true);
        try {
            const functions = getFunctions();
            const createPortalLink = httpsCallable(functions, 'createPortalLink');

            const result = await createPortalLink({
                returnUrl: window.location.origin + '/profile?tab=settings'
            });

            if (result.data.url) {
                // Redirect la Stripe Customer Portal
                window.location.href = result.data.url;
            } else {
                throw new Error('Nu s-a primit URL-ul portalului');
            }

        } catch (error) {
            console.error('Eroare portal Stripe:', error);
            let errorMessage = 'Eroare la deschiderea portalului.';

            if (error.code === 'unauthenticated') {
                errorMessage = 'Trebuie să fii autentificat.';
            } else if (error.code === 'not-found') {
                errorMessage = 'Nu s-a găsit informația customer. Contactează suportul.';
            } else if (error.message) {
                errorMessage = error.message;
            }

            alert(errorMessage + ' Te rugăm încearcă din nou.');
            setLoadingPortal(false);
        }
    };

    const getSubscriptionColor = (type) => {
        switch (type) {
            case 'premium':
                return {
                    bg: 'bg-purple-50',
                    border: 'border-purple-200',
                    text: 'text-purple-900',
                    accent: 'text-purple-600',
                    button: 'bg-purple-600 hover:bg-purple-700'
                };
            case 'standard':
                return {
                    bg: 'bg-blue-50',
                    border: 'border-blue-200',
                    text: 'text-blue-900',
                    accent: 'text-blue-600',
                    button: 'bg-blue-600 hover:bg-blue-700'
                };
            default:
                return {
                    bg: 'bg-slate-50',
                    border: 'border-slate-200',
                    text: 'text-slate-900',
                    accent: 'text-slate-600',
                    button: 'bg-slate-600 hover:bg-slate-700'
                };
        }
    };

    const colors = getSubscriptionColor(subscription.type);
    const daysRemaining = getDaysRemaining(subscription.endDate);

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Current Subscription */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Abonamentul Tău</h2>

                {subscription.type === 'none' ? (
                    <div className="text-center py-12">
                        <Crown size={48} className="mx-auto text-slate-300 mb-4" />
                        <h3 className="text-xl font-semibold text-slate-900 mb-2">
                            Nu ai un abonament activ
                        </h3>
                        <p className="text-slate-600 mb-6">
                            Alege un plan pentru a accesa toate funcționalitățile
                        </p>
                        <button
                            onClick={() => navigate('/pricing')}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg transition shadow-md"
                        >
                            Vezi Planurile
                        </button>
                    </div>
                ) : (
                    <div className={`${colors.bg} border-2 ${colors.border} rounded-xl p-6`}>
                        <div className="flex items-start justify-between mb-6">
                            <div className="flex items-center gap-4">
                                <div className={`w-16 h-16 ${colors.button} rounded-full flex items-center justify-center`}>
                                    <Crown size={32} className="text-white" />
                                </div>
                                <div>
                                    <h3 className={`text-2xl font-bold ${colors.text}`}>
                                        {subscription.type === 'premium' ? 'Premium' : 'Standard'}
                                    </h3>
                                    <p className={colors.accent}>
                                        {subscription.type === 'premium' ? '59€' : '29€'} / lună
                                    </p>
                                </div>
                            </div>
                            {subscription.autoRenew ? (
                                <span className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                                    <CheckCircle size={16} />
                                    Reînnoire Activă
                                </span>
                            ) : (
                                <span className="flex items-center gap-2 bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-sm font-semibold">
                                    <AlertCircle size={16} />
                                    Se Anulează
                                </span>
                            )}
                        </div>

                        {/* Subscription Details */}
                        <div className="grid md:grid-cols-2 gap-4 mb-6">
                            <div className="bg-white rounded-lg p-4 border border-slate-200">
                                <div className="flex items-center gap-3 mb-2">
                                    <Calendar size={20} className={colors.accent} />
                                    <span className="text-sm font-medium text-slate-600">Data expirării</span>
                                </div>
                                <p className="text-lg font-semibold text-slate-900">
                                    {formatDate(subscription.endDate)}
                                </p>
                            </div>

                            <div className="bg-white rounded-lg p-4 border border-slate-200">
                                <div className="flex items-center gap-3 mb-2">
                                    <Clock size={20} className={colors.accent} />
                                    <span className="text-sm font-medium text-slate-600">Timp rămas</span>
                                </div>
                                <p className="text-lg font-semibold text-slate-900">
                                    {daysRemaining} {daysRemaining === 1 ? 'zi' : 'zile'}
                                </p>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                                <span>Perioada curentă</span>
                                <span>{Math.max(0, Math.min(100, ((30 - daysRemaining) / 30) * 100)).toFixed(0)}% consumat</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2">
                                <div
                                    className={`${colors.button} h-2 rounded-full transition-all duration-500`}
                                    style={{ width: `${Math.max(0, Math.min(100, ((30 - daysRemaining) / 30) * 100))}%` }}
                                />
                            </div>
                        </div>

                        {/* Auto-Renew Info */}
                        <div className="bg-white rounded-lg p-4 border border-slate-200 mb-4">
                            <div className="flex items-start gap-3">
                                <CreditCard size={20} className={`${colors.accent} mt-1`} />
                                <div className="flex-1">
                                    <p className="font-semibold text-slate-900 mb-1">Reînnoire Automată</p>
                                    <p className="text-sm text-slate-600">
                                        {subscription.autoRenew
                                            ? 'Abonamentul se va reînnoi automat. Pentru a modifica setările de facturare, accesează Stripe Customer Portal.'
                                            : 'Abonamentul se va anula la expirare. Pentru a reactiva reînnoirea automată, accesează Stripe Customer Portal.'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Manage Subscription Button - FUNCȚIONAL */}
                        <div className="flex gap-4">
                                {subscription.type === 'standard' && (
                                    <button
                                        onClick={handleManageSubscription}
                                        disabled={loadingPortal}
                                        className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {loadingPortal ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                                Se încarcă...
                                            </>
                                        ) : (
                                            <>
                                                <Crown size={20} />
                                                Upgrade la Premium
                                            </>
                                        )}
                                    </button>
                                )}
                                <button
                                    onClick={handleManageSubscription}
                                    disabled={loadingPortal}
                                    className={`flex-1 ${colors.button} text-white font-semibold py-3 px-6 rounded-lg transition shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    {loadingPortal ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            Se încarcă...
                                        </>
                                    ) : (
                                        <>
                                            <ExternalLink size={20} />
                                            Gestionează Abonamentul
                                        </>
                                    )}
                                </button>
                        </div>

                        <p className="text-xs text-slate-500 text-center mt-3">
                            Vei fi redirecționat către Stripe Customer Portal pentru a gestiona metoda de plată, facturarea și anularea
                        </p>
                    </div>
                )}
            </div>

            {/* Notification Settings */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Setări Notificări</h2>

                <div className="space-y-4">
                    {/* Email Notifications */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                <Mail size={24} className="text-blue-600" />
                            </div>
                            <div>
                                <p className="font-semibold text-slate-900">Notificări Email</p>
                                <p className="text-sm text-slate-600">
                                    Primește actualizări importante prin email
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => handleToggle('emailNotifications')}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${localSettings.emailNotifications ? 'bg-green-600' : 'bg-slate-300'
                                }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${localSettings.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                            />
                        </button>
                    </div>

                    {/* Event Updates */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                <Bell size={24} className="text-purple-600" />
                            </div>
                            <div>
                                <p className="font-semibold text-slate-900">Evenimente și Noutăți</p>
                                <p className="text-sm text-slate-600">
                                    Fii la curent cu evenimentele și noutățile noastre
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => handleToggle('eventUpdates')}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${localSettings.eventUpdates ? 'bg-green-600' : 'bg-slate-300'
                                }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${localSettings.eventUpdates ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                            />
                        </button>
                    </div>
                </div>

                {/* Save Button */}
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {saving ? (
                        <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Se salvează...
                        </>
                    ) : (
                        <>
                            <CheckCircle size={20} />
                            Salvează Setări
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

// Clock icon component
const Clock = ({ size, className }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
    </svg>
);

export default SubscriptionSettings;